// orchestrator_fullstack/frontend/src/app/pages/LoginPage.tsx
import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

function getCachedRolesAndTenant(): { roles: string[]; tenant?: string } {
  try {
    const raw = localStorage.getItem("orchestrator.authme");
    if (!raw) return { roles: [] };
    const parsed = JSON.parse(raw);
    const me = parsed?.me;
    const roles = Array.isArray(me?.roles) ? me.roles : [];
    const tenant = typeof me?.tenant === "string" ? me.tenant : undefined;
    return { roles, tenant };
  } catch {
    return { roles: [] };
  }
}

function Divider({ label }: { label: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "18px 0" }}>
      <div style={{ height: 1, background: "rgba(0,0,0,0.08)", flex: 1 }} />
      <div style={{ fontSize: 12, color: "rgba(0,0,0,0.55)", fontWeight: 700 }}>{label}</div>
      <div style={{ height: 1, background: "rgba(0,0,0,0.08)", flex: 1 }} />
    </div>
  );
}

function HelpPill({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        padding: "6px 10px",
        borderRadius: 999,
        border: "1px solid rgba(0,0,0,0.12)",
        background: "rgba(255,255,255,0.9)",
        fontSize: 12,
        color: "rgba(0,0,0,0.72)",
        fontWeight: 600,
      }}
    >
      {children}
    </div>
  );
}

/**
 * Phase 1 (pure UI):
 * - Modern login UI
 * - "Continue with Google" button is disabled (tooltip explains why)
 * - JWT paste sign-in remains fully functional
 * - Redirects to ?next=... (defaults to /admin/overview)
 */
export default function LoginPage() {
  const [token, setToken] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(true);

  const { signInWithToken } = useAuth();
  const nav = useNavigate();
  const q = useQuery();
  
  //const next = q.get("next") || "/admin/overview";
  const defaultNext = "/admin/overview";
  const nextParam = q.get("next");
  const next = nextParam || defaultNext;

  const oauthDisabledMsg = "Admin must be configured for OAuth login";

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        padding: 24,
        background:
          "radial-gradient(1200px 600px at 20% 10%, rgba(59,130,246,0.10), transparent 55%)," +
          "radial-gradient(900px 500px at 90% 30%, rgba(16,185,129,0.10), transparent 60%)," +
          "linear-gradient(180deg, rgba(0,0,0,0.02), rgba(0,0,0,0.00))",
      }}
    >
      <div
        style={{
          width: "min(980px, 100%)",
          display: "grid",
          gridTemplateColumns: "1fr",
          gap: 16,
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr",
            gap: 16,
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1.1fr 0.9fr",
              gap: 16,
              alignItems: "stretch",
            }}
          >
            {/* Brand / left */}
            <div
              style={{
                borderRadius: 18,
                border: "1px solid rgba(0,0,0,0.10)",
                background: "rgba(255,255,255,0.70)",
                backdropFilter: "blur(10px)",
                padding: 22,
                boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div
                  aria-hidden
                  style={{
                    height: 34,
                    width: 34,
                    borderRadius: 12,
                    background:
                      "linear-gradient(135deg, rgba(59,130,246,0.9), rgba(16,185,129,0.9))",
                    boxShadow: "0 10px 20px rgba(0,0,0,0.10)",
                  }}
                />
                <div>
                  <div style={{ fontSize: 12, letterSpacing: 1.2, fontWeight: 800, color: "rgba(0,0,0,0.55)" }}>
                    ONE INTELLIGENCE
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 900, lineHeight: 1.1 }}>
                    Orchestrator Control Plane
                  </div>
                </div>
              </div>

              <div style={{ marginTop: 14, fontSize: 26, fontWeight: 950, lineHeight: 1.15 }}>
                Sign in to manage flows, ingress apps, providers, and observability.
              </div>

              <div style={{ marginTop: 10, color: "rgba(0,0,0,0.65)", fontSize: 14, lineHeight: 1.5 }}>
                Admin routes are <b>JWT-only</b>. Your role and tenant are resolved by <code>/v1/auth/me</code>,
                and the backend remains the source of truth. UI gating is convenience.
              </div>

              <div style={{ marginTop: 14, display: "flex", flexWrap: "wrap", gap: 10 }}>
                <HelpPill>JWT-only Admin</HelpPill>
                <HelpPill>/v1/auth/me source of truth</HelpPill>
                <HelpPill>Tenant isolation via DB RLS</HelpPill>
              </div>
            </div>

            {/* Sign-in / right */}
            <div
              style={{
                borderRadius: 18,
                border: "1px solid rgba(0,0,0,0.10)",
                background: "rgba(255,255,255,0.90)",
                padding: 20,
                boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 900 }}>Sign in</div>
                  <div style={{ fontSize: 12, color: "rgba(0,0,0,0.60)", marginTop: 2 }}>
                    Choose a provider or use the JWT fallback.
                  </div>
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: "rgba(0,0,0,0.55)",
                    border: "1px solid rgba(0,0,0,0.10)",
                    background: "rgba(0,0,0,0.02)",
                    padding: "6px 10px",
                    borderRadius: 999,
                    fontWeight: 700,
                  }}
                  title={next}
                >
                  Next: {next}
                </div>
              </div>

              {/* Provider buttons (disabled in Phase 1) */}
              <div style={{ marginTop: 14, display: "grid", gap: 10 }}>
                <button
                  type="button"
                  disabled
                  title={oauthDisabledMsg}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: 14,
                    border: "1px solid rgba(0,0,0,0.12)",
                    background: "rgba(0,0,0,0.03)",
                    color: "rgba(0,0,0,0.45)",
                    cursor: "not-allowed",
                    fontWeight: 900,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 10,
                  }}
                >
                  <span
                    aria-hidden
                    style={{
                      height: 18,
                      width: 18,
                      borderRadius: 6,
                      background: "linear-gradient(135deg, #4285F4, #34A853)",
                      opacity: 0.55,
                    }}
                  />
                  Continue with Google
                </button>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  <button
                    type="button"
                    disabled
                    title={oauthDisabledMsg}
                    style={{
                      padding: "10px 12px",
                      borderRadius: 14,
                      border: "1px solid rgba(0,0,0,0.12)",
                      background: "rgba(0,0,0,0.03)",
                      color: "rgba(0,0,0,0.45)",
                      cursor: "not-allowed",
                      fontWeight: 900,
                    }}
                  >
                    GitHub
                  </button>
                  <button
                    type="button"
                    disabled
                    title={oauthDisabledMsg}
                    style={{
                      padding: "10px 12px",
                      borderRadius: 14,
                      border: "1px solid rgba(0,0,0,0.12)",
                      background: "rgba(0,0,0,0.03)",
                      color: "rgba(0,0,0,0.45)",
                      cursor: "not-allowed",
                      fontWeight: 900,
                    }}
                  >
                    Microsoft
                  </button>
                </div>
              </div>

              <Divider label="OR" />

              {/* Advanced JWT */}
              <button
                type="button"
                onClick={() => setShowAdvanced((v) => !v)}
                style={{
                  width: "100%",
                  textAlign: "left",
                  padding: "10px 12px",
                  borderRadius: 14,
                  border: "1px solid rgba(0,0,0,0.10)",
                  background: "rgba(0,0,0,0.02)",
                  fontWeight: 900,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                }}
              >
                <span>Advanced: Paste JWT</span>
                <span style={{ fontSize: 12, opacity: 0.7 }}>{showAdvanced ? "Hide" : "Show"}</span>
              </button>

              {showAdvanced && (
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    if (busy) return;

                    const t = token.trim();
                    if (!t) {
                      setErr("Paste a JWT token.");
                      return;
                    }

                    setBusy(true);
                    setErr(null);
                    try {
                      await signInWithToken(t);
                      // If caller provided ?next=..., respect it.
                      if (nextParam) {
                        nav(nextParam, { replace: true });
                        return;
                      }

                      // Otherwise choose a sane landing page based on resolved roles.
                      const { roles } = getCachedRolesAndTenant();
                      const has = (r: string) => roles.includes(r);

                      if (has("platform-admin") || has("admin")) {
                        nav("/admin/overview", { replace: true });
                      } else if (has("operator")) {
                        nav("/admin/observability/platform", { replace: true });
                      } else {
                        // viewer / unknown
                        nav("/login?next=%2Fadmin%2Fobservability%2Fplatform", { replace: true });
                      }
                    } catch (e2: any) {
                      setErr(e2?.message || "Sign-in failed");
                    } finally {
                      setBusy(false);
                    }
                  }}
                  style={{ marginTop: 10 }}
                >
                  <textarea
                    value={token}
                    onChange={(e) => setToken(e.target.value)}
                    placeholder="eyJhbGciOiJSUzI1NiIs..."
                    rows={8}
                    style={{
                      width: "100%",
                      borderRadius: 14,
                      border: "1px solid rgba(0,0,0,0.12)",
                      padding: 12,
                      fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                      fontSize: 12,
                      outline: "none",
                    }}
                  />

                  {err && (
                    <div
                      style={{
                        marginTop: 10,
                        padding: 12,
                        background: "rgba(255,0,0,0.08)",
                        borderRadius: 14,
                        color: "#b00020",
                        fontWeight: 700,
                        fontSize: 12,
                        lineHeight: 1.4,
                        border: "1px solid rgba(176,0,32,0.18)",
                      }}
                    >
                      {err}
                    </div>
                  )}

                  <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
                    <button
                      type="submit"
                      disabled={busy || !token.trim()}
                      style={{
                        flex: 1,
                        padding: "10px 14px",
                        fontWeight: 950,
                        borderRadius: 14,
                        border: "1px solid rgba(0,0,0,0.10)",
                        background: busy ? "rgba(0,0,0,0.06)" : "rgba(0,0,0,0.88)",
                        color: busy ? "rgba(0,0,0,0.55)" : "white",
                        cursor: busy ? "not-allowed" : "pointer",
                      }}
                    >
                      {busy ? "Signing in..." : "Continue"}
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        setToken("");
                        setErr(null);
                      }}
                      style={{
                        padding: "10px 14px",
                        borderRadius: 14,
                        border: "1px solid rgba(0,0,0,0.12)",
                        background: "rgba(0,0,0,0.02)",
                        fontWeight: 900,
                      }}
                    >
                      Clear
                    </button>
                  </div>

                  <div style={{ marginTop: 10, fontSize: 12, color: "rgba(0,0,0,0.62)" }}>
                    Tip: After sign-in, your roles/tenant are fetched from <code>/v1/auth/me</code>. If your token is
                    expired, paste a fresh one.
                  </div>
                </form>
              )}
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "center" }}>
            <div style={{ fontSize: 12, color: "rgba(0,0,0,0.55)" }}>
              Need access? Ask a <b>platform-admin</b> to map your email/group → role and re-login.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}